import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Typography } from '@mui/material';
import { Button, Drawer } from '@biq/apex-ui';
import { useNavigate } from 'react-router-dom';
import { HelixLoader } from '../../../../components';
import { useAuth } from '../../../../core';
import { ROLES } from '../../../../core/contexts/permissions';
import {
    useDictionaryList,
    useJobById,
    useReceiptList,
} from '../../../../hooks';
import { Dictionary, getJobDetailsType, ReceiptRoot } from '../../../../lib';
import {
    getChipDisplay,
    JobRowData,
} from '../job-manager-list/job-list-helpers';
import {
    anchorPoint,
    DetailAccordion,
    detailAccordions,
    jobDetailsDrawerStyle,
    ReceiptButtonStyle,
    smartContractReceiptsQueryProps,
} from './job-manager-details-helper';
import JobManagerDetailsAccordion from './JobManagerDetailsAccordion';
import MissingSmartContractReceipt from './MissingSmartContractReceipt';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';

interface JobManagerDetailsDrawerProps {
    onDrawerClose: () => void;
    job: JobRowData;
    receiptType: 'admin' | 'user';
}

const JobManagerDetailsDrawer = ({
    onDrawerClose,
    job,
    receiptType,
}: JobManagerDetailsDrawerProps): React.JSX.Element => {
    const [open, setOpen] = useState<boolean>(false);
    const [selectedChain, setSelectedChain] = useState<string>(job.dictName);
    const [selectedReceiptId, setSelectedReceiptId] = useState<string>('');
    const [displayMissingReceiptModal, setDisplayMissingReceiptModal] =
        useState<boolean>(false);
    const [expanded, setExpanded] = useState<string | boolean>('');
    const { hasRequiredRoles } = useAuth();
    const navigate = useNavigate();
    const {
        data: dictionaryList,
        isFetched: areDictionaryFetched,
        refetch: getDictionaryList,
        isError: isDictionaryError,
    } = useDictionaryList({
        queryProps: smartContractReceiptsQueryProps,
    });
    const {
        data: receiptList,
        isFetched: areReceiptsFetched,
        refetch: getReceiptList,
        isError: isReceiptsError,
    } = useReceiptList({
        chain: selectedChain,
        contractType: 'smartcontracts',
        receiptType,
        enabled: false,
    });
    const { data: jobDetailData, isLoading: isJobDetailLoading } = useJobById({
        jobId: job?.jobId ?? '',
        enabled: !!job,
    });

    const displayErrorStatus: boolean = job.status === 'COMPLETED_ERROR';
    const displayReceiptButton: boolean =
        job.status !== 'COMPLETED_ERROR' && job.name === 'SmartContractExecJob';

    const jobID: string = job?.jobId ?? 'N/A';
    const title: string = `ID: ${jobID}`;
    const formattedJobType = getJobDetailsType(job.name);
    const hasViewReceiptPermission = hasRequiredRoles([
        ROLES.user,
        ROLES.readOnly,
    ]);
    const displayReceiptAllowed =
        hasViewReceiptPermission && displayReceiptButton;
    const accordions: DetailAccordion[] = [
        { ...detailAccordions[0], data: jobDetailData },
        { ...detailAccordions[1], data: jobDetailData?.results?.results },
        { ...detailAccordions[2], data: jobDetailData?.metadata },
        { ...detailAccordions[3], data: jobDetailData?.results?.error },
    ];

    useEffect(() => {
        setOpen(!!job);
    }, [job]);

    useEffect(() => {
        // Display missing receipt modal if we clicked on the receipt button and get
        // either a dictionary or receipts endpoint error.
        if (
            selectedChain &&
            selectedReceiptId &&
            (isDictionaryError || isReceiptsError)
        ) {
            setDisplayMissingReceiptModal(true);
        }
        if (
            areReceiptsFetched &&
            receiptList &&
            areDictionaryFetched &&
            dictionaryList &&
            selectedChain &&
            selectedReceiptId
        ) {
            const receipts: ReceiptRoot[] = receiptList.data ?? [];
            const receiptFound: ReceiptRoot | undefined = receipts.find(
                (item: ReceiptRoot) => item.header?.id === selectedReceiptId
            );

            const dictionaries: Dictionary[] = dictionaryList.data ?? [];
            const dictionaryFound: Dictionary | undefined = dictionaries.find(
                (dictionary: Dictionary) => dictionary.name === selectedChain
            );

            // If both current receipt and dictionary are found navigate to smart contracts page,
            // otherwise, display the missing receipt modal.
            if (receiptFound && dictionaryFound) {
                navigate(
                    `${SMART_CONTRACT_PATHS.LANDING_PAGE}/receipt/${selectedChain}/${selectedReceiptId}`,
                    {
                        state: receiptFound,
                    }
                );
            } else {
                setDisplayMissingReceiptModal(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        areReceiptsFetched,
        receiptList,
        areDictionaryFetched,
        dictionaryList,
        selectedChain,
        selectedReceiptId,
        isDictionaryError,
        isReceiptsError,
    ]);

    const handleReceiptButtonClick = (): void => {
        if (displayReceiptAllowed) {
            setSelectedChain(job.dictName);
            setSelectedReceiptId(jobDetailData?.receipt?.header?.id ?? '');
            getDictionaryList();
            getReceiptList();
        }
    };

    const handleMissingModalClose = (): void => {
        setSelectedReceiptId('');
        setSelectedChain('');
        setDisplayMissingReceiptModal(false);
    };

    const handleDrawerClose = (): void => {
        onDrawerClose();
        handleMissingModalClose();
    };

    const handleChange = (isExpanded: boolean, panel: string): void => {
        setExpanded(isExpanded ? panel : false);
    };

    const subheader: React.JSX.Element = (
        <Box sx={jobDetailsDrawerStyle.subheaderContainer}>
            <Box sx={jobDetailsDrawerStyle.subheaderInnerContainer}>
                <Typography style={jobDetailsDrawerStyle.subheaderStyle}>
                    {formattedJobType}
                </Typography>
                <Box sx={jobDetailsDrawerStyle.chipContainer}>
                    {getChipDisplay(job.status)}
                </Box>
            </Box>
            {displayReceiptAllowed && (
                <Box sx={jobDetailsDrawerStyle.buttonContainer}>
                    <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        style={ReceiptButtonStyle}
                        onClick={handleReceiptButtonClick}
                    >
                        View Receipt
                    </Button>
                </Box>
            )}
        </Box>
    );

    return (
        <Drawer
            closable
            open={open}
            drawerType={'rounded'}
            anchor={anchorPoint}
            sx={jobDetailsDrawerStyle.detailsDrawer}
            onClose={handleDrawerClose}
            title={title}
            titleStyles={jobDetailsDrawerStyle.titleStyle}
            subheaderChildren={subheader}
        >
            {isJobDetailLoading ? (
                <HelixLoader />
            ) : (
                <Box sx={jobDetailsDrawerStyle.accordionWrapper}>
                    {accordions.map((accordion) => {
                        if (!displayErrorStatus && accordion.error) return null;

                        return (
                            <JobManagerDetailsAccordion
                                key={`${accordion.name}_${accordion.accordionSumID}`}
                                expanded={expanded === accordion.name}
                                onChange={(_, isExpanded): void =>
                                    handleChange(isExpanded, accordion.name)
                                }
                                accordionAriaControls={
                                    accordion.accordionAriaControls
                                }
                                accordionSumID={accordion.accordionSumID}
                                accordionHeader={accordion.accordionHeader}
                                data={accordion.data}
                                displayErrorIcon={accordion?.error ?? false}
                            />
                        );
                    })}
                </Box>
            )}
            {displayMissingReceiptModal && (
                <MissingSmartContractReceipt
                    onClose={handleMissingModalClose}
                />
            )}
        </Drawer>
    );
};

export default JobManagerDetailsDrawer;
